import { delay, putResolve, select } from "redux-saga/effects";
import {
  GetPaymentFailed,
  GetPaymentPending,
  GetPaymentResultEnum,
  CarPaymentFinalizeResult,
  CarPaymentFinalizeResultSucceeded,
  COMPLETE_BUY_CARS,
  CompleteBuyCarProperties,
  ITrackingProperties,
  PaymentError as LegacyPaymentError,
} from "redmond";
import {
  ErrorCode,
  Payment,
  PaymentError,
  ProductError,
  Product,
  PurchaseError,
  PurchaseErrorEnum,
  FulfillResponse,
  FulfillResponseEnum,
  FulfillFailure,
  FulfillSuccess,
} from "@b2bportal/purchase-api";
import {
  travelAppInterface,
  TravelAppInterfaceEventName,
  TravelAppInterfaceFunnelType,
} from "@capone/common";
import { brexBridge, getIsBrexApp } from "b2b-base/src/brex/bridge";
import type { GroundBookingResult } from "@b2bportal/ground-api";
import dayjs from "dayjs";
import { pollFinalized } from "../../../../api/v1/book/book-flow/pollFinalized";
import { IStoreState } from "../../../../reducers/types";
import {
  getCompleteBuyCarProperties,
  getSession,
  getUseV1PurchaseFlow,
  getv1FulfillSession,
} from "../../reducer";
import {
  setPollConfirmationDetailsCallStateFailure,
  setPollConfirmationDetailsCallStateSuccess,
  setConfirmationDetails,
  IPollConfirmationDetails,
} from "../../actions/actions";
import { trackEvent } from "../../../../api/v1/analytics/trackEvent";
import { pollFinalizedV1 } from "../../../../api/v2/book/book-flow/pollFinalized";
import { buildCarBookingData } from "../../utils/buildCarBookingData";

const getCardDeclineProps = (errors: PurchaseError[]) => {
  const cardError = errors.find(
    (e) => e.Error === PurchaseErrorEnum.PaymentError && (e as PaymentError).value.type === Payment.Card
  );
  if (!cardError) return {};
  const { messageTitle, messageBody } = (cardError as PaymentError).value.value;
  return messageTitle && messageBody
    ? { card_decline_reason_title: messageTitle, card_decline_reason_body: messageBody }
    : {};
};

const getCarBookingReservationId = (
  booking: GroundBookingResult | undefined
) =>
  booking?.confirmationInfo?.supplierBookingId ||
  booking?.confirmationInfo?.providerBookingId ||
  booking?.groundBookingId;

const toLegacyErrorString = (error: LegacyPaymentError): string =>
  error.code ||
  error.message ||
  error.msg ||
  error.PaymentError ||
  JSON.stringify(error);

const toErrorString = (error: PurchaseError): string => {
  switch (error.Error) {
    case PurchaseErrorEnum.ErrorCode: {
      const e = error as ErrorCode;
      return `${e.code}${e.message ? ` - ${e.message}` : ""}`;
    }
    case PurchaseErrorEnum.PaymentError: {
      const paymentError = error as PaymentError;
      return toLegacyErrorString(
        paymentError.value.value as LegacyPaymentError
      );
    }
    case PurchaseErrorEnum.ProductError: {
      const purchaseError = error as ProductError;
      return toLegacyErrorString(
        purchaseError.value.value as LegacyPaymentError
      );
    }
    default:
      return PurchaseErrorEnum[error.Error];
  }
};

export function* pollConfirmationDetailsSaga({
  agentFee,
  initialTime,
}: IPollConfirmationDetails) {
  try {
    const state: IStoreState = yield select();
    const sessionToken = getSession(state);
    const v1FulfillSessionToken = getv1FulfillSession(state);
    const version = getUseV1PurchaseFlow(state) ? "v1" : "v0";

    const completeBuyCarProperties = getCompleteBuyCarProperties(state);

    if (!sessionToken || (version === "v1" && !v1FulfillSessionToken)) {
      throw new Error("Session token is not present.");
    }

    const delayTimes = [1000];
    let pollFailed = false;
    let index = 0;

    while (!pollFailed) {
      yield delay(delayTimes[index]);
      if (version === "v0") {
        const finalizedCheckedResponse: GetPaymentPending = yield pollFinalized(
          sessionToken
        ).catch((e) => {
          // [CMKT-1150] do not error out on network errors, instead retry
          if (e.response.status !== 200) {
            return Promise.resolve({
              data: { result: GetPaymentResultEnum.Pending },
              status: 200,
              statusText: "Gateway timeout",
              headers: [],
              config: {},
            });
          }
          return Promise.reject(e);
        });

        switch (finalizedCheckedResponse.result) {
          case GetPaymentResultEnum.Failed: {
            pollFailed = true;
            const failedResponse = finalizedCheckedResponse as GetPaymentFailed;
            trackEvent({
              eventName: COMPLETE_BUY_CARS,
              properties: {
                ...completeBuyCarProperties.properties,
                success: false,
                agent_booking_fee_amount_usd: agentFee,
                failure_reason:
                  failedResponse.errors.length > 0
                    ? failedResponse.errors
                        .map((error) => toLegacyErrorString(error))
                        .join(" - ")
                    : "Poll Confirmation Details response returned an error and the given error code is not handleable.",
                booking_duration: dayjs().diff(initialTime, "seconds", true),
              },
              encryptedProperties: [
                ...completeBuyCarProperties.encryptedProperties,
              ],
            });
            if (failedResponse.errors.length > 0) {
              yield putResolve(
                setPollConfirmationDetailsCallStateFailure(
                  failedResponse.errors
                )
              );
              return;
            }
            yield putResolve(setPollConfirmationDetailsCallStateFailure([]));
            throw new Error(
              "Price quote checked response returned an error and the given error code is not handleable."
            );
          }
          case GetPaymentResultEnum.Pending: {
            const priceQuoteResponsePoll =
              finalizedCheckedResponse as GetPaymentPending;
            if (priceQuoteResponsePoll.result === GetPaymentResultEnum.Failed) {
              pollFailed = true;
              yield putResolve(
                setPollConfirmationDetailsCallStateFailure([])
              );
            }
            break;
          }
          case GetPaymentResultEnum.Succeeded: {
            const finalizedResponse =
              finalizedCheckedResponse as CarPaymentFinalizeResultSucceeded;
            switch (finalizedResponse.result) {
              case GetPaymentResultEnum.Succeeded:
                yield putResolve(
                  setConfirmationDetails(
                    (finalizedResponse as CarPaymentFinalizeResult).booking
                  )
                );
                yield putResolve(setPollConfirmationDetailsCallStateSuccess());
                if (getIsBrexApp()) {
                  const v0ReservationId = getCarBookingReservationId(
                    (finalizedResponse as CarPaymentFinalizeResult)
                      .booking as unknown as GroundBookingResult
                  );
                  if (v0ReservationId) {
                    brexBridge.emit("BOOKING_COMPLETE", {
                      reservationId: v0ReservationId,
                    });
                  }
                }
                trackEvent({
                  eventName: COMPLETE_BUY_CARS,
                  properties: {
                    ...completeBuyCarProperties.properties,
                    success: true,
                    agent_booking_fee_amount_usd: agentFee,
                    ground_booking_id: (
                      finalizedResponse as CarPaymentFinalizeResult
                    ).booking.groundBookingId,
                    ...(finalizedResponse as CarPaymentFinalizeResult).booking
                      .trackingPropertiesV2?.properties,
                    booking_duration: dayjs().diff(
                      initialTime,
                      "seconds",
                      true
                    ),
                  },
                  encryptedProperties: [
                    ...completeBuyCarProperties.encryptedProperties,
                    (finalizedResponse as CarPaymentFinalizeResult).booking
                      .trackingPropertiesV2?.encryptedProperties ?? "",
                  ],
                });
                travelAppInterface.sendEvent({
                  name: TravelAppInterfaceEventName.BOOKING_COMPLETE,
                  funnel: TravelAppInterfaceFunnelType.CARS,
                  // redmond's CarPaymentFinalizeResult.booking is type-skewed from
                  // @b2bportal/ground-api's GroundBookingResult (same runtime shape).
                  data: buildCarBookingData(
                    (finalizedResponse as CarPaymentFinalizeResult)
                      .booking as unknown as GroundBookingResult
                  ),
                });
                return;
              default:
                pollFailed = true;
                yield putResolve(
                  setPollConfirmationDetailsCallStateFailure([])
                );
                trackEvent({
                  eventName: COMPLETE_BUY_CARS,
                  properties: {
                    ...completeBuyCarProperties,
                    success: false,
                    agent_booking_fee_amount_usd: agentFee,
                    failure_reason:
                      "Poll Confirmation Details Success Response Failed",
                    booking_duration: dayjs().diff(
                      initialTime,
                      "seconds",
                      true
                    ),
                  },
                });
                throw new Error("Poll Finalized Failed");
            }
          }
          default:
            break;
        }
      } else {
        if (!v1FulfillSessionToken) {
          throw new Error("Session token is not present.");
        }

        const finalizedCheckedResponse: FulfillResponse = yield pollFinalizedV1(
          { req: v1FulfillSessionToken }
        ).catch((e) => {
          // [CMKT-1150] do not error out on network errors, instead retry
          if (e.response.status !== 200) {
            return Promise.resolve({
              data: { result: FulfillResponseEnum.Pending },
              status: 200,
              statusText: "Gateway timeout",
              headers: [],
              config: {},
            });
          }
          return Promise.reject(e);
        });
        switch (finalizedCheckedResponse.FulfillResponse) {
          case FulfillResponseEnum.Failure: {
            pollFailed = true;
            const failedResponse = finalizedCheckedResponse as FulfillFailure;
            const cardDeclineProps = getCardDeclineProps(failedResponse.errors);
            trackEvent({
              eventName: COMPLETE_BUY_CARS,
              properties: {
                ...completeBuyCarProperties.properties,
                ...cardDeclineProps,
                success: false,
                agent_booking_fee_amount_usd: agentFee,
                failure_reason:
                  failedResponse.errors.length > 0
                    ? failedResponse.errors
                        .map((error) => toErrorString(error))
                        .join(" - ")
                    : "Poll Confirmation Details response returned an error and the given error code is not handleable.",
                booking_duration: dayjs().diff(initialTime, "seconds", true),
              },
              encryptedProperties: [
                ...completeBuyCarProperties.encryptedProperties,
              ],
            });
            if (failedResponse.errors.length > 0) {
              yield putResolve(
                setPollConfirmationDetailsCallStateFailure(
                  failedResponse.errors
                )
              );
              return;
            }
            yield putResolve(setPollConfirmationDetailsCallStateFailure([]));
            throw new Error(
              "Price quote checked response returned an error and the given error code is not handleable."
            );
          }
          case FulfillResponseEnum.Pending:
            break;
          case FulfillResponseEnum.Success: {
            const finalizedResponse =
              finalizedCheckedResponse as FulfillSuccess;
            const fulfillProduct = finalizedResponse.products.find(
              (p) => p.type === Product.Ground
            );
            yield putResolve(setConfirmationDetails(fulfillProduct?.value));
            yield putResolve(setPollConfirmationDetailsCallStateSuccess());
            if (getIsBrexApp()) {
              const v1ReservationId = getCarBookingReservationId(
                fulfillProduct?.value as GroundBookingResult | undefined
              );
              if (v1ReservationId) {
                brexBridge.emit("BOOKING_COMPLETE", {
                  reservationId: v1ReservationId,
                });
              }
            }
            trackEvent({
              eventName: COMPLETE_BUY_CARS,
              properties: {
                ...completeBuyCarProperties.properties,
                success: true,
                agent_booking_fee_amount_usd: agentFee,
                ground_booking_id: fulfillProduct?.value?.groundBookingId,
                ...(fulfillProduct?.value?.trackingPropertiesV2?.properties ?? {}),
                booking_duration: dayjs().diff(initialTime, "seconds", true),
              },
              encryptedProperties: [
                ...completeBuyCarProperties.encryptedProperties,
                fulfillProduct?.value?.trackingPropertiesV2?.encryptedProperties ?? "",
              ],
            });
            const groundBooking = fulfillProduct?.value as
              | GroundBookingResult
              | undefined;
            if (groundBooking) {
              travelAppInterface.sendEvent({
                name: TravelAppInterfaceEventName.BOOKING_COMPLETE,
                funnel: TravelAppInterfaceFunnelType.CARS,
                data: buildCarBookingData(groundBooking),
              });
            }
            return;
          }
          default:
            pollFailed = true;
            yield putResolve(setPollConfirmationDetailsCallStateFailure([]));
            trackEvent({
              eventName: COMPLETE_BUY_CARS,
              properties: {
                ...completeBuyCarProperties,
                success: false,
                agent_booking_fee_amount_usd: agentFee,
                failure_reason:
                  "Poll Confirmation Details Success Response Failed",
                booking_duration: dayjs().diff(initialTime, "seconds", true),
              },
            });
            throw new Error("Poll Finalized Failed");
        }
      }
      // if we want to give up on polling we should instead direct the user to my trips
      if (index >= delayTimes.length) {
        window.location.pathname = "/trips";
      }

      if (index !== delayTimes.length - 1) {
        index++;
      }
    }
  } catch {
    const completeBuyCarProperties: ITrackingProperties<CompleteBuyCarProperties> =
      yield select(getCompleteBuyCarProperties);

    trackEvent({
      eventName: COMPLETE_BUY_CARS,
      properties: {
        ...completeBuyCarProperties.properties,
        success: true,
        agent_booking_fee_amount_usd: agentFee,
        failure_reason: "Poll Confirmation Details Network Call Failed",
      },
      encryptedProperties: [...completeBuyCarProperties.encryptedProperties],
    });
    yield putResolve(setPollConfirmationDetailsCallStateFailure([]));
  }
}
